<script lang="ts" setup>
import { ref, useFormat, useDayjs, watch, onBeforeUnmount, computed, useElementVisibility, useI18n } from '#imports'
import { MessageType, type MessagesComponents as components, type UIIconProps } from '@core/types'

export interface PromotionUserNotificationCardProps {
    notification: components['schemas']['RenderedNotification']
    view?: 'card' | 'list'
    errors: any
    icon: UIIconProps['name']
}

const props = withDefaults(defineProps<PromotionUserNotificationCardProps>(), {
    view: 'card'
})

const emit = defineEmits([
    'markAsRead',
    'callAction',
])

const { t } = useI18n()
const dayjs = useDayjs()
const { asRelativeTime } = useFormat()

const loading = ref(false)

const card = ref(null)
const cardIsVisible = useElementVisibility(card, { threshold: 0.66 })
const notificationTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const hasStartedTimer = ref(false)

const hasError = computed(() => props.notification.id in (props?.errors?.value ?? {}))
const timeAgo = asRelativeTime(props.notification.displayed_created_at ?? props.notification.created_at, { timeFormat: 'short', length: 1 })

const expiredAt = asRelativeTime(props.notification.expires_at, { timeFormat: 'long', length: 1 })

const isExpired = computed(() => {
    if (!props.notification.expires_at) return false

    return dayjs.tz().isAfter(dayjs(props.notification.expires_at).tz())
})

const hasActions = computed(() => !!props.notification?.dismissAction || !!props.notification?.acceptAction)

// Computed (not plain consts): the notification prop can be replaced in place
// (XMPP subscriber updates, live previews), so actions must stay reactive.
const singleActionText = computed<string | null>(() => props.notification?.dismissAction?.text || props.notification?.acceptAction?.text || null)
const singleAction = computed<components['schemas']['RenderedNotificationAction'] | null>(() => props.notification?.dismissAction || props.notification?.acceptAction || null)

const onClick = async (action: components['schemas']['RenderedNotificationAction'], notification: components['schemas']['RenderedNotification']) => {
    loading.value = true

    if (!action) {
        loading.value = false

        return
    }

    emit('callAction', { action, notification })

    loading.value = false
}

function startTimer() {
    // Prevent starting multiple timers for the same notification
    if (hasStartedTimer.value || notificationTimer.value) {
        return
    }

    hasStartedTimer.value = true
    notificationTimer.value = setTimeout(() => {
        emit('markAsRead')
        notificationTimer.value = null
    }, 1000)
}

function stopTimer() {
    if (notificationTimer.value) {
        clearTimeout(notificationTimer.value)
        notificationTimer.value = null
    }
}

// Some icons need to be pushed down and left in the icon-box because of visual off-balance
// Add more when necessary
const iconPlacementOffset = MessageType.game_launch as string === props.notification.type

let stopWatch: (() => void) | null = null

if (import.meta.client) {
    stopWatch = watch(cardIsVisible, (isVisible) => {
        if (isVisible && !props.notification.read) {
            startTimer()
        }
        else {
            stopTimer()
        }
    }, { immediate: true })
}

onBeforeUnmount(() => {
    stopTimer()
    if (stopWatch) stopWatch()
})
</script>

<template>
    <article
        ref="card"
        class="notification-card"
        :class="{ 'notification-card--list': props.view === 'list', 'notification-card--card': props.view === 'card', 'notification-card--disabled': isExpired }"
    >
        <div class="notification-card__content content">
            <div v-if="!props.notification.read && props.view === 'card'" class="content__unread" />
            <div v-if="isExpired && props.notification.expires_at" class="content__expired">
                {{ t('promotions.notifications.expired') }} - {{ expiredAt }}
            </div>
            <div v-if="props.view === 'card'" class="content__delivered-at out-in-wrap">
                <Transition name="fade">
                    <span v-if="timeAgo">
                        {{ timeAgo }}
                    </span>
                    <span v-else class="time-skeleton" />
                </Transition>
            </div>

            <div class="content__body">
                <div class="content__icon" :class="{ 'content__icon--offset': iconPlacementOffset }">
                    <div class="content__icon-background">
                        <UIIcon :name="props.icon" size="24" />
                    </div>
                </div>

                <div class="content__main">
                    <div class="content__title">
                        <h3 class="use-brand-font">
                            {{ props.notification.title }}
                        </h3>
                        <div v-if="props.view === 'list'" class="out-in-wrap">
                            <Transition name="fade">
                                <span v-if="timeAgo" class="content__delivered-at">
                                    <span v-if="!props.notification.read" class="content__unread" />
                                    {{ timeAgo }}
                                </span>
                                <span v-else class="time-skeleton" />
                            </Transition>
                        </div>
                    </div>
                    <div v-if="props.notification.body" class="content__message">
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <p v-html="props.notification.body" />
                    </div>
                    <Transition
                        name="fade"
                        appear
                    >
                        <UIAlert
                            v-if="hasError"
                            :message="t('core.defaultErrorMessage')"
                            class="content__error"
                            :duration="5000"
                        />
                    </Transition>
                </div>

                <div
                    v-if="hasActions"
                    class="content__actions"
                >
                    <template v-if="!!props.notification?.dismissAction && !!props.notification?.acceptAction">
                        <UIButton as="cta" size="sm" @click="onClick(props.notification.acceptAction, props.notification)">
                            {{ props.notification.acceptAction.text }}
                        </UIButton>
                        <UIButton
                            color="secondary"
                            outlined
                            size="sm"
                            @click="onClick(props.notification.dismissAction, props.notification)"
                        >
                            {{ props.notification.dismissAction.text }}
                        </UIButton>
                    </template>
                    <UIButton
                        v-else-if="singleAction && singleActionText"
                        color="secondary"
                        size="sm"
                        @click="onClick(singleAction, props.notification)"
                    >
                        {{ singleActionText }}
                    </UIButton>
                </div>
            </div>
        </div>

        <Transition
            name="fade"
            appear
        >
            <div
                v-if="loading"
                class="notification-card-overlay"
            >
                <UILoader :text="t('promotions.pleaseWait')" />
            </div>
        </Transition>
    </article>
</template>

<style lang="scss" scoped>
.notification-card {
    position: relative;
    display: grid;
    width: 100%;
    background-color: var(--notification-card-bg, var(--background));
    border-radius: var(--border-radius);
    overflow: hidden;

    .content {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 15px 25px;

        &__unread {
            position: absolute;
            left: 10px;
            width: 8px;
            height: 8px;
            background-color: var(--danger);
            border-radius: 50%;
        }

        &__expired {
            align-self: flex-start;
            font-size: $font-size-xs;
            padding: 5px 8px;
            margin-bottom: 8px;
            color: var(--on-warning);
            background-color: var(--warning);
            border: 1px solid;
            border-color: var(--border-warning-light, color.adjust($brand-warning-border, $lightness: -5%));
            border-radius: var(--border-radius);
        }

        &__body {
            display: flex;
        }

        &__icon {
            width: 45px;
            aspect-ratio: 1;
            color: var(--notification-card-icon-color, var(--secondary));

            &-background {
                display: flex;
                justify-content: center;
                align-items: center;
                width: 45px;
                height: 45px;
                border-radius: 50%;
                color: inherit;
                background-color: var(--notification-card-icon-bg, var(--surface));
            }

            :deep(.icon) {
                width: 100%;
                height: 100%;
                max-width: 24px;
                max-height: 24px;
            }

            &--offset {
                .icon {
                    margin-top: 2px;
                    margin-right: 2px;
                }
            }
        }

        &__main {
            display: flex;
            flex-direction: column;
            flex: 1;
            min-width: 0;
        }

        &__title {
            h3 {
                margin: 0;
                font-size: $font-size-base;
                font-weight: $text-medium;
            }
        }

        &__message {
            color: var(--on-background);

            p {
                margin: 0;
                font-size: $font-size-sm;
            }
        }

        .time-skeleton {
            display: block;
            width: 75px;
            background-color: rgb(0 0 0 / 15%);
            border-radius: var(--border-radius);
            height: 1em;
            animation: pulse-opacity 3s ease-in-out forwards;
        }

        &__error {
            margin-bottom: 0 !important;
            z-index: 1;
        }

        &__actions {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            gap: 15px;

            button {
                flex: 1;
            }
        }
    }

    &--list .content {
        gap: 0;
        padding: 15px 25px 15px 15px;

        .content__body {
            gap: 15px;
            align-items: flex-start;
        }

        .content__icon {
            width: 28px;
            margin-top: -7px;

            &-background {
                width: 28px;
                height: 28px;
                background-color: transparent;
            }

            :deep(.icon) {
                max-height: 18px;
            }
        }

        .content__title {
            display: inline-flex;
            margin-right: 20px;
            position: relative;

            h3 {
                font-size: $font-size-xs + 1;
            }

            .content__unread {
                position: absolute;
                left: -12px;
                top: 2px;
            }
        }

        .content__delivered-at {
            position: relative;
            height: 12px;
            margin-left: 20px;
            margin-bottom: 8px;
            font-size: $font-size-xxs;
            color: $notification-time-stamp-color;
            white-space: nowrap;
        }

        .content__message p {
            font-size: $font-size-xs;
        }

        .content__actions {
            align-self: center;
            flex-direction: column;
        }
    }

    &--card .content {
        .content__body {
            position: relative;
            flex-direction: column;
            flex: 1;
            gap: 10px;
        }

        .content__icon {
            position: absolute;
            top: 0;
            right: 0;
        }

        .content__title {
            min-height: 45px;
            padding-right: 55px;
        }

        .content__main {
            gap: 10px;
        }

        .content__unread {
            margin-top: 4px;
        }

        .content__delivered-at {
            height: $font-size-base;
            font-size: $font-size-xs;
            color: $notification-time-stamp-color;
        }
    }

    &-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: $border-radius;
        background: rgba(255, 255, 255, 0.75);
        backdrop-filter: blur(3px);
        -webkit-backdrop-filter: blur(3px);
        z-index: 13;
    }

    &--disabled {
        user-select: none;
        pointer-events: none;

        .content {
            &__title,
            &__message,
            &__actions {
                opacity: 0.5;
            }
        }
    }
}

@keyframes pulse-opacity {
    0% {
        opacity: 0;
    }

    16.66% {
        opacity: 1;
    }

    33.33% {
        opacity: 0;
    }

    50% {
        opacity: 1;
    }

    66.66% {
        opacity: 0;
    }

    83.33% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}
</style>
